import * as React from 'react';
import makeComponentTrashable, { TrashableProps } from 'trashable-react';
import { TrashableContext } from './TrashableContext';

// TODO: implement makeComponentTrashable as a functional HOC to support hooks more naturally

/**
 * A HOC that
 *  - copies `registerPromise` prop of the input component and save it in a `TrashableContext`;
 *  - returns the input component
 * so children of the input component can all access `registerPromise` via `useTrashablePromise`
 *
 * Usage:
 *  For any component
 *    const Foo = () => <Bar/>;
 *  Given that it will be wrapped before use
 *    copyTrashableToContext(withTrashableContext(Foo));
 *  It and its children can access registerPromise via context instead of props passing
 *    const Bar = () => {
 *      const {registerPromise} = useTrashablePromise();
 *      // ...
 *    }
 */
export const copyTrashablePromiseToContext =
  <P extends TrashableProps = TrashableProps>(
    WrappedComponent: React.ComponentType<P>,
  ): React.FunctionComponent<P> =>
  (props: P) => {
    const { registerPromise } = props;

    if (!registerPromise) {
      console.error(
        `The prop registerPromise is undefined, 
    please verify you are passing a component 
    wrapped with makeComponentTrashable.`,
      );
    }

    return (
      <TrashableContext.Provider value={{ registerPromise }}>
        <WrappedComponent {...props} />
      </TrashableContext.Provider>
    );
  };

/**
 * A shortcut to wrap a component within a `TrashableContext`.
 */
export const withTrashableContext = <P extends {}>(
  C: React.FunctionComponent<P>,
): React.FunctionComponent<P> =>
  // @ts-ignore: the input doesn't need to know about the `registerPromise` prop
  makeComponentTrashable(copyTrashablePromiseToContext(C));
